/**
 * Fetches account deletion job status with the Supabase access token (no useApiClient).
 * Used right after sign-in before React auth refs update, and shared with AccountDeletionAccessGate.
 */

import axios from 'axios'
import { API_URL, API_ENDPOINTS } from '../../../constants/api'
import { logger } from '../../helpers/logger.helper'
import { authLiveDebug } from '../helpers/authLiveDebug.helper'
import { isIncompleteDeletionLastErrorCode } from '../../privacy/accountDeletionJob.constants'
import {
  SIGN_IN_BANNER_ACCOUNT_DELETION_INCOMPLETE,
  SIGN_IN_BANNER_ACCOUNT_REMOVED,
  SIGN_IN_BANNER_DELETION_IN_PROGRESS,
} from '../../privacy/privacyAndData.constants'

const DEFAULT_MAX_ATTEMPTS = 3
const RETRY_DELAY_MS = 250

export type AccountDeletionStatusParse =
  | { ok: true; jobStatus: null; lastErrorCode: null }
  | { ok: true; jobStatus: string; lastErrorCode: string | null }
  | { ok: false }

export function parseAccountDeletionStatusResponse(data: unknown): AccountDeletionStatusParse {
  if (data === null || typeof data !== 'object') return { ok: false }
  const body = data as {
    success?: boolean
    data?: { status?: string; lastErrorCode?: string | null } | null
  }
  if (body.success !== true) return { ok: false }
  if (body.data === null || body.data === undefined) {
    return { ok: true, jobStatus: null, lastErrorCode: null }
  }
  const status = body.data.status
  if (typeof status !== 'string') return { ok: false }
  const rawCode = body.data.lastErrorCode
  let lastErrorCode: string | null = null
  if (rawCode === undefined || rawCode === null) {
    lastErrorCode = null
  } else if (typeof rawCode === 'string') {
    lastErrorCode = rawCode
  } else {
    return { ok: false }
  }
  return { ok: true, jobStatus: status, lastErrorCode }
}

export function deletionJobStatusBlocksApp(
  jobStatus: string | null,
  lastErrorCode: string | null = null
): boolean {
  if (jobStatus === 'PENDING' || jobStatus === 'PROCESSING' || jobStatus === 'COMPLETED') {
    return true
  }
  if (jobStatus === 'FAILED' && lastErrorCode !== null && isIncompleteDeletionLastErrorCode(lastErrorCode)) {
    return true
  }
  return false
}

export function signInBannerForBlockedDeletionJobStatus(
  status: string,
  lastErrorCode: string | null = null
): string {
  if (status === 'COMPLETED') return SIGN_IN_BANNER_ACCOUNT_REMOVED
  if (status === 'FAILED' && lastErrorCode !== null && isIncompleteDeletionLastErrorCode(lastErrorCode)) {
    return SIGN_IN_BANNER_ACCOUNT_DELETION_INCOMPLETE
  }
  return SIGN_IN_BANNER_DELETION_IN_PROGRESS
}

export async function fetchAccountDeletionJobStatusOnce(
  accessToken: string
): Promise<
  { ok: true; jobStatus: string | null; lastErrorCode: string | null } | { ok: false }
> {
  const url = `${API_URL}${API_ENDPOINTS.users.accountDeletionStatus}`
  const t0 = Date.now()
  authLiveDebug('DeletionStatusApi request start', { endpoint: API_ENDPOINTS.users.accountDeletionStatus })
  try {
    // Avoid Cache-Control / Pragma on cross-origin requests: browsers preflight and
    // api.zporti.com CORS must list those headers; cache bust via query param only.
    const res = await axios.get(url, {
      params: { _: String(Date.now()) },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      validateStatus: (s) => s >= 200 && s < 300,
      timeout: 8000,
    })
    const parsed = parseAccountDeletionStatusResponse(res.data)
    if (!parsed.ok) {
      authLiveDebug('DeletionStatusApi unparseable response', { ms: Date.now() - t0 })
      logger.warn('[DeletionStatusApi] unparseable response', { ms: Date.now() - t0 })
      return { ok: false }
    }
    authLiveDebug('DeletionStatusApi ok', {
      jobStatus: parsed.jobStatus,
      ms: Date.now() - t0,
    })
    logger.info('[DeletionStatusApi] ok', {
      jobStatus: parsed.jobStatus, ms: Date.now() - t0,
    })
    return { ok: true, jobStatus: parsed.jobStatus, lastErrorCode: parsed.lastErrorCode }
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined
    authLiveDebug('DeletionStatusApi request FAILED', {
      status,
      message: err instanceof Error ? err.message : String(err),
      ms: Date.now() - t0,
    })
    logger.warn('[DeletionStatusApi] request failed', {
      status,
      message: err instanceof Error ? err.message : String(err),
      ms: Date.now() - t0,
    })
    return { ok: false }
  }
}

export async function fetchAccountDeletionJobStatusWithRetries(
  accessToken: string,
  maxAttempts: number = DEFAULT_MAX_ATTEMPTS
): Promise<{ ok: true; jobStatus: string | null; lastErrorCode: string | null } | { ok: false }> {
  authLiveDebug('DeletionStatusApi retries start', { maxAttempts })
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    authLiveDebug('DeletionStatusApi attempt', { attempt, maxAttempts })
    const result = await fetchAccountDeletionJobStatusOnce(accessToken)
    if (result.ok) {
      authLiveDebug('DeletionStatusApi retries success', { attempt })
      return result
    }
    if (attempt < maxAttempts) {
      authLiveDebug('DeletionStatusApi retry after delay', { attempt, delayMs: RETRY_DELAY_MS * attempt })
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * attempt))
    }
  }
  authLiveDebug('DeletionStatusApi retries EXHAUSTED', { maxAttempts })
  return { ok: false }
}
